import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Flex,
  HStack,
  Text,
  VStack,
  useToast,
} from "@chakra-ui/react";
import { Target } from "@phosphor-icons/react";
import { useState } from "react";
import { useQueryClient } from "react-query";
import { ImportData } from "../../../../components/ImportData";
import {
  foundationV2Keys,
  useDownloadFoundationV2TargetTemplate,
  useUploadFoundationV2TargetStructure,
} from "../../../../hooks/query/issb";
import { showErrorToast, showSuccessToast } from "../../../../utils";

/**
 * The datapoints this organisation wants collected for one workstream, and the way to change them.
 *
 * Above the table rather than below it, because it governs what is beneath it: the extraction is steered by
 * this list, and the table renders as this list. A panel a reader met after seeing an unexplained set of gaps
 * would be in the wrong place.
 *
 * Per workstream since ESG-4003 — the datapoints wanted for targets are not those wanted for emissions, and a
 * shared list showed each one the other's gaps.
 *
 * `showChrome={false}` drops the outer bordered box, for a caller that has already framed it (the detail
 * page's accordion) and would otherwise show two nested borders.
 */
export default function TargetStructurePanel({ structure, canEdit, questionGroup, showChrome = true }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [issues, setIssues] = useState([]);

  /*
    Error mapping copied from `ImportItemsButton`, which is the one that distinguishes a POI 500 from a
    template message from a row-level failure. `file-argument-not-valid` is the code
    `FileNotMatchTheTemplateException` becomes, and its `parameters.message` is written for a human — it
    names the column that is wrong. Showing a generic "import failed" over the top of it would throw away
    the only part of the response worth reading.
  */
  const uploadConfig = {
    onSuccess: (result) => {
      setIssues(result?.issues || []);
      // This workstream's key, not every structure: an upload for targets must not refetch the emissions one.
      queryClient.invalidateQueries(foundationV2Keys.targetStructure(questionGroup));
      showSuccessToast(
        toast,
        `${result?.structure?.datapointCount ?? 0} datapoints loaded${
          result?.replacedPrevious ? ", replacing your previous structure" : ""
        }`
      );
    },
    onError: (err) => {
      setIssues([]);
      const data = err?.response?.data;
      showErrorToast(
        toast,
        data?.errorCode === "file-argument-not-valid"
          ? data?.parameters?.message ?? err.message
          : data?.parameters?.message ?? data?.message ?? err.message
      );
    },
  };

  const chrome = showChrome
    ? { borderWidth: "1px", borderColor: "gray.200", borderRadius: "md", p: 4 }
    : {};

  return (
    <Box {...chrome}>
      <Flex justify="space-between" align="flex-start" gap={4} wrap="wrap">
        <VStack align="flex-start" spacing={1}>
          <HStack spacing={2}>
            <Target size={16} />
            <Text fontSize="body.m" fontWeight="600" color="gray.700">
              {questionGroup ? `Target structure — ${questionGroup}` : "Target structure"}
            </Text>
            {structure ? (
              <>
                <Badge colorScheme="green" variant="subtle">
                  {structure.datapointCount} datapoint
                  {structure.datapointCount === 1 ? "" : "s"}
                </Badge>
                {/* Says whose choice this is. Without it, datapoints nobody picked would read as the
                    organisation's own declared requirements. */}
                {structure.isDefault ? (
                  <Badge colorScheme="gray" variant="outline">
                    default
                  </Badge>
                ) : null}
              </>
            ) : null}
          </HStack>
          <Text fontSize="body.xs" color="gray.500" maxW="640px">
            {!structure ? (
              // Barely reachable now — every organisation is seeded a default — so this covers only the case
              // where the shipped template could not be read at all. Says what is degraded rather than
              // pretending nothing is.
              <>
                No target structure could be loaded, so extraction will choose its own names and the table
                below will show only what was found. Uploading a structure fixes this.
              </>
            ) : structure.isDefault ? (
              <>
                A standard set of ESG disclosures, applied to every organisation until you replace it.
                Extractions look for these by name, and the table below shows every one of them — filled or
                still missing. Download it, edit it and upload it to make it yours.
              </>
            ) : (
              <>
                The figures you want collected, from{" "}
                <Text as="span" fontWeight="600">
                  {structure.sourceFileName || structure.name || "an uploaded workbook"}
                </Text>
                . Extractions look for these by name, and the table below shows every one of them — filled or
                still missing.
              </>
            )}
          </Text>
          {structure ? (
            <Text fontSize="xs" color="gray.400">
              Version {structure.structureVersion}
              {/* Which version a figure was matched against is recorded per row, so this is not cosmetic:
                  it is the reference an older save's provenance points at. */}
            </Text>
          ) : null}
        </VStack>

        {canEdit ? (
          <ImportData
            importDataBtnId="foundation-target-structure"
            importDataHook={useUploadFoundationV2TargetStructure}
            importDataConfig={uploadConfig}
            /* Both carry the workstream, or an upload would replace whichever structure the server defaults
               to and the download would hand out the wrong blank workbook. */
            importDataParams={{ questionGroup }}
            downloadTemplateHook={useDownloadFoundationV2TargetTemplate}
            downloadTemplateParams={questionGroup}
            tooltip={
              "Column A is the section, B the datapoint name, C its expected unit.\n" +
              "Value, Prior year and Source are ignored — fill those in on your own copy.\n" +
              "Must be .xlsx: the older .xls format cannot be read."
            }
          />
        ) : null}
      </Flex>

      {issues.length > 0 ? (
        /*
          Rendered here rather than through `IngestIssuesTable`. That component adds one to `rowIndex`
          because ai-ingest's producers send a 0-based index; this parser sends the spreadsheet row number
          as Excel prints it, so reusing the table would point the user one line below every problem.
        */
        <Alert status="warning" mt={3} borderRadius="md" alignItems="flex-start">
          <AlertIcon />
          <Box>
            <Text fontSize="body.s" fontWeight="600">
              Loaded, with {issues.length} thing{issues.length === 1 ? "" : "s"} to look at
            </Text>
            <VStack align="stretch" spacing={0} mt={1}>
              {issues.slice(0, 8).map((issue, index) => (
                <Text key={`${issue.rowIndex}-${issue.code}-${index}`} fontSize="body.xs" color="gray.700">
                  • Row {issue.rowIndex}: {issue.message}
                </Text>
              ))}
            </VStack>
            {issues.length > 8 ? (
              <Text fontSize="body.xs" color="gray.500" mt={1}>
                …and {issues.length - 8} more.
              </Text>
            ) : null}
          </Box>
        </Alert>
      ) : null}
    </Box>
  );
}
